import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Building2, Upload, Loader2, Save, ImageOff, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import {
  getConfiguracionEmpresa,
  updateConfiguracionEmpresa,
  uploadLogoEmpresa,
} from './Services/parametrizacion.services';

const MAX_MB = 3;

// Mismos valores por defecto que el schema del backend. Se repiten aquí para
// que la vista previa del encabezado tenga sentido antes de guardar por
// primera vez, que es cuando el backend aún no tiene documento.
const DEFAULTS = {
  nombreEmpresa: '',
  nit: '',
  direccion: '',
  telefono: '',
  email: '',
  sitioWeb: '',
  ciudad: '',
  logoUrl: '',
  colorPrimario: '#6D28D9',
  colorSecundario: '#111827',
  pieDePagina: '',
  representanteLegal: '',
  cargoRepresentante: '',
};

const CAMPOS = Object.keys(DEFAULTS);

const ParametrizacionGeneralPage = () => {
  const { toast } = useToast();
  const [form, setForm] = useState(DEFAULTS);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [subiendo, setSubiendo] = useState(false);
  const [preview, setPreview] = useState('');
  const [archivo, setArchivo] = useState(null);

  const cargar = async () => {
    setCargando(true);
    try {
      const res = await getConfiguracionEmpresa();
      const data = res?.data || {};
      // Se vuelve a armar el objeto con la lista de campos conocida para no
      // meter en el estado lo que venga del servidor: un campo extra o un
      // null inesperado se perderían al guardar.
      const limpio = {};
      for (const c of CAMPOS) limpio[c] = data[c] ?? DEFAULTS[c];
      setForm(limpio);
      setPreview(data.logoUrl || '');
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (campo) => (e) => setForm({ ...form, [campo]: e.target.value });

  const elegirArchivo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_MB * 1024 * 1024) {
      toast({
        title: 'Archivo muy grande',
        description: `El logo debe pesar menos de ${MAX_MB} MB.`,
        variant: 'destructive',
      });
      e.target.value = '';
      return;
    }
    setArchivo(file);
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const quitarLogo = () => {
    setArchivo(null);
    setPreview('');
    setForm({ ...form, logoUrl: '' });
  };

  const guardar = async (e) => {
    e.preventDefault();
    if (!form.nombreEmpresa.trim()) {
      toast({
        title: 'Faltan datos',
        description: 'El nombre de la empresa es obligatorio: encabezará todos los documentos.',
        variant: 'destructive',
      });
      return;
    }

    setGuardando(true);
    try {
      let logoUrl = form.logoUrl;
      // Igual que en Firmas: primero se sube el archivo y se guarda la URL
      // devuelta, para que el registro nunca apunte a una imagen no subida.
      if (archivo) {
        setSubiendo(true);
        const res = await uploadLogoEmpresa(archivo);
        if (!res?.state) throw new Error(res?.message || 'No se pudo subir el logo');
        logoUrl = res.data?.url;
      }

      const payload = { ...form, logoUrl };
      const res = await updateConfiguracionEmpresa(payload);
      if (!res?.state) throw new Error(res?.message || 'No se pudo guardar');

      setArchivo(null);
      setPreview(logoUrl);
      setForm({ ...form, logoUrl });
      toast({ title: 'Éxito', description: 'Configuración de la empresa guardada.' });
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setSubiendo(false);
      setGuardando(false);
    }
  };

  const ocupado = guardando || subiendo;

  if (cargando) {
    return <p className="text-sm text-muted-foreground">Cargando configuración...</p>;
  }

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
      <form onSubmit={guardar} className="space-y-4">
        {/* Vista previa: el encabezado es lo primero que se ve mal si el logo
            quedó cortado o el color no contrasta. */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Vista previa del encabezado</CardTitle>
            <CardDescription>
              Así se verá la cabecera de los certificados laborales y los demás documentos del
              módulo.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div
              className="rounded-lg border bg-white p-6"
              style={{ borderTop: `4px solid ${form.colorPrimario || '#6D28D9'}` }}
            >
              <div className="flex items-start gap-4">
                {preview ? (
                  <img
                    src={preview}
                    alt="Logo"
                    className="h-16 w-auto max-w-[180px] object-contain"
                  />
                ) : (
                  <div className="flex h-16 w-32 items-center justify-center rounded border border-dashed text-xs text-gray-400">
                    Sin logo
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p
                    className="text-lg font-bold leading-tight"
                    style={{ color: form.colorPrimario || '#6D28D9' }}
                  >
                    {form.nombreEmpresa || 'Nombre de la empresa'}
                  </p>
                  <p className="text-xs text-gray-600">
                    {[form.nit && `NIT ${form.nit}`, form.direccion, form.telefono, form.email]
                      .filter(Boolean)
                      .join(' · ') || 'NIT, dirección, teléfono y correo'}
                  </p>
                </div>
              </div>
              {form.pieDePagina && (
                <p className="mt-4 border-t pt-2 text-center text-[10px] text-gray-500">
                  {form.pieDePagina}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Building2 className="h-4 w-4" /> Datos de la empresa
            </CardTitle>
            <CardDescription>
              Esta es la empresa que emite los documentos. El nombre debe ser el de la sociedad,
              no el de la lavandería donde trabaja el empleado.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="cfg-nombre">Nombre de la empresa *</Label>
                <Input
                  id="cfg-nombre"
                  value={form.nombreEmpresa}
                  onChange={set('nombreEmpresa')}
                  placeholder="Ej. OptyLav S.A.S."
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cfg-nit">NIT</Label>
                <Input
                  id="cfg-nit"
                  value={form.nit}
                  onChange={set('nit')}
                  placeholder="Ej. 900.123.456-7"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="cfg-direccion">Dirección</Label>
                <Input
                  id="cfg-direccion"
                  value={form.direccion}
                  onChange={set('direccion')}
                  placeholder="Ej. Calle 100 # 70-15, Medellín"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cfg-telefono">Teléfono</Label>
                <Input
                  id="cfg-telefono"
                  value={form.telefono}
                  onChange={set('telefono')}
                  placeholder="Ej. (604) 123 4567"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cfg-email">Correo</Label>
                <Input
                  id="cfg-email"
                  type="email"
                  value={form.email}
                  onChange={set('email')}
                  placeholder="contacto@empresa.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cfg-web">Sitio web</Label>
                <Input
                  id="cfg-web"
                  value={form.sitioWeb}
                  onChange={set('sitioWeb')}
                  placeholder="https://www.empresa.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cfg-ciudad">Ciudad de expedición</Label>
                <Input
                  id="cfg-ciudad"
                  value={form.ciudad}
                  onChange={set('ciudad')}
                  placeholder="Ej. Medellín"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Presentación</CardTitle>
            <CardDescription>
              Logo y colores que se aplican a la cabecera de los documentos generados.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="cfg-logo">Logo</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="cfg-logo"
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={elegirArchivo}
                  className="h-auto py-2"
                />
                {preview && (
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    title="Quitar logo"
                    onClick={quitarLogo}
                    disabled={ocupado}
                  >
                    <ImageOff className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                PNG, JPG o WEBP de hasta {MAX_MB} MB. Ideal fondo transparente y horizontal.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="cfg-color-primario">Color primario</Label>
                <div className="flex items-center gap-2">
                  <input
                    id="cfg-color-primario"
                    type="color"
                    value={form.colorPrimario}
                    onChange={set('colorPrimario')}
                    className="h-10 w-14 cursor-pointer rounded-md border border-input bg-background p-1"
                  />
                  <Input
                    value={form.colorPrimario}
                    onChange={set('colorPrimario')}
                    className="font-mono"
                    aria-label="Color primario en hexadecimal"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Encabezado y acentos. Formato hexadecimal, por ejemplo #6D28D9.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="cfg-color-secundario">Color secundario</Label>
                <div className="flex items-center gap-2">
                  <input
                    id="cfg-color-secundario"
                    type="color"
                    value={form.colorSecundario}
                    onChange={set('colorSecundario')}
                    className="h-10 w-14 cursor-pointer rounded-md border border-input bg-background p-1"
                  />
                  <Input
                    value={form.colorSecundario}
                    onChange={set('colorSecundario')}
                    className="font-mono"
                    aria-label="Color secundario en hexadecimal"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Texto secundario y datos de contacto.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Contenido legal</CardTitle>
            <CardDescription>
              Representante y pie de página. En Colombia el certificado debe estar a nombre de
              alguien con facultad para firmarlo.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="cfg-rep">Representante legal</Label>
                <Input
                  id="cfg-rep"
                  value={form.representanteLegal}
                  onChange={set('representanteLegal')}
                  placeholder="Ej. María Fernanda Gómez"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cfg-cargo-rep">Cargo del representante</Label>
                <Input
                  id="cfg-cargo-rep"
                  value={form.cargoRepresentante}
                  onChange={set('cargoRepresentante')}
                  placeholder="Ej. Gerente de Recursos Humanos"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="cfg-pie">Pie de página</Label>
              <Textarea
                id="cfg-pie"
                value={form.pieDePagina}
                onChange={set('pieDePagina')}
                placeholder="Ej. Documento generado electrónicamente por OptyLav. Tiene plena validez ante la DIAN."
                rows={3}
              />
              <p className="text-xs text-muted-foreground">
                Texto que se imprime al final de cada documento.
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="flex items-center justify-end gap-2">
          <Button type="button" variant="outline" onClick={cargar} disabled={ocupado}>
            <RotateCcw className="mr-2 h-4 w-4" /> Descartar
          </Button>
          <Button type="submit" disabled={ocupado}>
            {ocupado ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Guardar
          </Button>
        </div>
      </form>
    </motion.div>
  );
};

export default ParametrizacionGeneralPage;